/**
 * useForecastRange Hook
 *
 * Fetches forecast data for a date range (multiple days) for timeline charts.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { forecastsApi } from "@/api/services/forecasts";
import type {
  ForecastDataItem,
  ForecastVariableData,
  ForecastDataResponse,
} from "@/api/types/forecasts";
import type { StormImpactModel } from "@/types";
import { isThreatSlug } from "@/config/weather-threats";

export interface ForecastRangeDataPoint {
  date: string;
  timestamp: number;
  [key: string]: string | number | ForecastRangeDataPoint["threats"];
  threats?: Array<{
    id: string;
    slug: string;
    name: string;
    value: string;
    isActive: boolean;
    description: string;
  }>;
}

interface UseForecastRangeParams {
  nodeId?: string;
  modelId?: string;
  weatherModelId?: string;
  startDate?: Date;
  forecastRange?: number;
  run?: string;
  stormImpactModel?: StormImpactModel;
}

export function useForecastRange(params: UseForecastRangeParams) {
  const {
    nodeId,
    modelId,
    weatherModelId,
    startDate,
    forecastRange,
    run,
    stormImpactModel,
  } = params;

  const isEnabled =
    !!nodeId &&
    !!modelId &&
    !!weatherModelId &&
    !!startDate &&
    forecastRange !== undefined &&
    !!run;

  let endDate: Date | null = null;
  if (isEnabled && startDate) {
    endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (forecastRange || 0));
  }

  const { data, isLoading, error, isPlaceholderData } = useQuery({
    queryKey: [
      "forecasts",
      "range",
      {
        nodeId,
        modelId,
        weatherModelId,
        startDate: startDate?.toISOString(),
        forecastRange,
        run,
      },
    ],
    queryFn: async () => {
      const response = await forecastsApi.forecasts.list({
        modelId: modelId!,
        weatherModelId: weatherModelId!,
        geographicNodeIds: nodeId!,
        min_date: format(startDate!, "yyyy-MM-dd"),
        max_date: format(endDate!, "yyyy-MM-dd"),
        run: run!,
        limit: 500,
      });
      
      if (!response.items || response.items.length === 0) {
        return [];
      }

      const grouped: Record<string, ForecastDataItem> = {};

      const isOrgRoot = nodeId === "org-root";
      const directMatches = response.items.filter(
        (item) => item.geographic_node_id === nodeId,
      );
      // Org / opco selections use parent node IDs; API returns district rows.
      const itemsToProcess =
        isOrgRoot || directMatches.length === 0
          ? response.items
          : directMatches;

      const sortedItems = [...itemsToProcess].sort((a, b) => {
        const dateCompare = a.forecast_date.localeCompare(b.forecast_date);
        if (dateCompare !== 0) return dateCompare;
        return a.geographic_node_id.localeCompare(b.geographic_node_id);
      });

      sortedItems.forEach((forecast: ForecastDataItem) => {
        const key = forecast.forecast_date;

        if (!grouped[key]) {
          grouped[key] = forecast;
        }
      });

      const rangeData: ForecastRangeDataPoint[] = Object.values(grouped).map(
        (forecast) => {
          const dataPoint: ForecastRangeDataPoint = {
            date: forecast.forecast_date,
            timestamp: new Date(forecast.forecast_date).getTime(),
          };

          // Extract threats and variables
          const threats: ForecastRangeDataPoint["threats"] = [];

          // Add variables as data points
          forecast.variable_values?.forEach(
            (varValue: ForecastVariableData) => {
              const variableKey = varValue.variable_slug;
              const value = parseFloat(varValue.value);

              const isThreat =
                (varValue.variable_type === "threat" || isThreatSlug(variableKey)) &&
                parseFloat(String(varValue.value)) === 1;
              if (isThreat) {
                threats.push({
                  id: `${forecast.forecast_date}-${variableKey}`,
                  slug: variableKey,
                  name: varValue.variable_name || variableKey
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase()),
                  value: varValue.value,
                  isActive: true,
                  description: `${variableKey.replace(/_/g, " ")} threat`,
                });
              }

              if (!isNaN(value)) {
                dataPoint[variableKey] = value;
              }
            },
          );

          if (threats.length > 0) {
            dataPoint.threats = threats;
          }

          return dataPoint;
        },
      );

      // Sort by date
      rangeData.sort((a, b) => a.timestamp - b.timestamp);

      return rangeData;
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes (grid data updates with runs)
    placeholderData: keepPreviousData, // Keep showing old data while new query loads
  });

  return {
    data: data || [],
    loading: isLoading,
    error,
    isPlaceholderData: !!isPlaceholderData,
  };
}

export default useForecastRange;
